import { Monitor, Moon, Sun } from "lucide-react";
import { useTheme, type ThemeChoice } from "../../lib/themeMode";
import { cn } from "../../lib/cn";

const OPTIONS: { value: ThemeChoice; label: string; icon: typeof Sun }[] = [
  { value: "system", label: "System", icon: Monitor },
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
];

/** System / Light / Dark, as a radio group (arrow keys move the choice). `compact` shows icons
 * only, for narrow rails; the labels stay for screen readers and as tooltips. `onRail` is for
 * the always-dark Superadmin rail. */
export function ThemeSwitch({ compact, onRail, className }: { compact?: boolean; onRail?: boolean; className?: string }) {
  const { choice, set } = useTheme();
  return (
    <div role="radiogroup" aria-label="Theme" className={cn("inline-flex rounded-[10px] p-0.5", onRail ? "bg-white/10" : "bg-neutral-100", className)}>
      {OPTIONS.map((o, i) => {
        const active = o.value === choice;
        const Icon = o.icon;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={o.label}
            title={o.label}
            tabIndex={active ? 0 : -1}
            onClick={() => set(o.value)}
            onKeyDown={(e) => {
              if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
              e.preventDefault();
              const next = OPTIONS[(i + (e.key === "ArrowRight" ? 1 : OPTIONS.length - 1)) % OPTIONS.length]!;
              set(next.value);
              (e.currentTarget.parentElement?.children[OPTIONS.indexOf(next)] as HTMLButtonElement | undefined)?.focus();
            }}
            className={cn(
              "inline-flex h-8 items-center justify-center gap-1.5 rounded-[8px] px-2.5 text-xs font-semibold transition-colors",
              onRail ? (active ? "bg-white/20 text-white" : "text-white/70 hover:text-white") : active ? "bg-lift text-neutral-900 shadow-[0_1px_3px_rgb(15_23_42/0.12)]" : "text-neutral-600 hover:text-neutral-900",
            )}
          >
            <Icon className="h-3.5 w-3.5" aria-hidden="true" />
            {!compact && <span>{o.label}</span>}
          </button>
        );
      })}
    </div>
  );
}
